/* ==========================================================================
   Product card behaviour: pack selection and add to cart.

   Both are progressive enhancements over markup that already works. The variant
   buttons are real buttons and the form is a real product form, so with this
   file blocked the card still adds to the cart — it just does it with a page
   load, and the pack pills fall back to the card's default variant.

   One delegated listener for the whole document rather than one per card. A
   collection page can carry forty of these and forty pairs of listeners is
   forty things to tear down when a section re-renders in the theme editor.
   ========================================================================== */
(function () {
  'use strict';

  var ADD_URL = (window.routes && window.routes.cart_add_url) || '/cart/add';

  function closestCard(el) {
    return el.closest ? el.closest('.cs-pcard') : null;
  }

  /* ---------------------------------------------------------------------
     Pack selection — swap the id the form posts, and the price shown.
     --------------------------------------------------------------------- */
  function selectVariant(button) {
    var card = closestCard(button);
    if (!card) return;

    card.querySelectorAll('[data-cs-variant]').forEach(function (b) {
      var on = b === button;
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    var idField = card.querySelector('[data-cs-atc-id]');
    if (idField) idField.value = button.dataset.csVariant;

    var price = card.querySelector('[data-cs-price]');
    if (price && button.dataset.price) price.textContent = button.dataset.price;

    // Compare-at and the saving travel together: a strikethrough with no
    // percentage next to it reads as a mistake.
    var compare = card.querySelector('[data-cs-compare]');
    var save = card.querySelector('[data-cs-save]');
    var hasOffer = !!button.dataset.compare;

    if (compare) {
      compare.textContent = hasOffer ? button.dataset.compare : '';
      compare.hidden = !hasOffer;
    }
    if (save) {
      save.textContent = hasOffer ? '(' + button.dataset.save + '% OFF)' : '';
      save.hidden = !hasOffer;
    }

    var atc = card.querySelector('[data-cs-atc-button]');
    if (atc) atc.disabled = button.dataset.available === 'false';
  }

  /* ---------------------------------------------------------------------
     Which cart UI to render into.

     Dawn ships two, and which one exists is decided by the CART TYPE setting:
     `drawer` renders <cart-drawer>, `notification` renders <cart-notification>,
     and `page` renders neither. This shop is on `drawer`, so <cart-notification>
     is never in the document.

     That is what sent every card add-to-cart to /cart. This function used to
     look up <cart-notification> alone and bail out when it was missing, and
     bailing out means no preventDefault, so the browser posted the form the
     old-fashioned way and landed on the cart page. The drawer was built,
     mounted and working — the cards were the only thing not talking to it.

     Both elements expose the same two methods, so the drawer is preferred, the
     notification is honoured if the setting is ever changed, and `page` still
     falls through to a normal form post, which is the correct behaviour there.
     --------------------------------------------------------------------- */
  function cartTarget() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') return drawer;

    var notification = document.querySelector('cart-notification');
    if (notification && typeof notification.renderContents === 'function') return notification;

    return null;
  }

  /* ---------------------------------------------------------------------
     Add to cart — post it, then hand the response to the cart UI the theme
     is configured for, so the card does not invent a second cart UI.
     --------------------------------------------------------------------- */
  function addToCart(form, event) {
    var target = cartTarget();

    // Nothing to render into, so let the browser submit the form and land on
    // the cart page.
    if (!target) return;

    event.preventDefault();

    var button = form.querySelector('[data-cs-atc-button]');
    var label = form.querySelector('[data-cs-atc-label]');
    var original = label ? label.textContent : '';

    if (button) {
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
    }

    var body = new FormData(form);
    if (typeof target.getSectionsToRender === 'function') {
      body.append('sections', target.getSectionsToRender().map(function (s) { return s.id; }));
      body.append('sections_url', window.location.pathname);
    }

    fetch(ADD_URL, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status) {
          // Shopify answers an out-of-stock or invalid line with a status code
          // in the body rather than an HTTP error.
          if (label) label.textContent = data.description || data.message || 'Unavailable';
          return;
        }
        // `renderContents` clears `is-empty` from the drawer's inner panel but
        // NOT from <cart-drawer> itself, and the element-level class is the one
        // that drives the empty-cart layout: it hides `.drawer__header`, which
        // is where the title and the CLOSE BUTTON live, and switches the panel
        // to a single centred row so the line items collapse. A shopper whose
        // cart was empty on page load would open the drawer, see no item and no
        // way out of it. Dawn's own product form clears it for the same reason
        // (assets/product-form.js) — the cards have to do it too.
        if (target.classList.contains('is-empty')) target.classList.remove('is-empty');

        target.renderContents(data);
      })
      .catch(function () {
        // Network failed. Fall back to the thing that always works.
        form.submit();
      })
      .then(function () {
        if (button) {
          button.classList.remove('is-loading');
          button.removeAttribute('aria-busy');
        }
        if (label && label.textContent !== original) {
          setTimeout(function () { label.textContent = original; }, 2400);
        }
      });
  }

  document.addEventListener('click', function (e) {
    var variant = e.target.closest && e.target.closest('[data-cs-variant]');
    if (variant) {
      e.preventDefault();
      selectVariant(variant);
    }
  });

  document.addEventListener('submit', function (e) {
    var form = e.target.closest && e.target.closest('[data-cs-atc]');
    if (form) addToCart(form, e);
  });
})();
